import { useEffect, useRef, useState } from "react";
import Modal, { ModalProps } from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import ModalTitle from "../Modal/ModalTitle";
import { cn } from "@/shared/utils/cn";
import { IconsVariant } from "../Icon/Icon";
import usePosOrderPayment from "@/shared/atoms/usePosOrderPaymentAtom";
import PosPaymentEmailOrSms from "../PosCart/PosPaymentEmailOrSms";
import PosCartOrderButton from "../PosCart/PosCartOrderButton";
import usePosOrderHook from "@/shared/hooks/usePosOrderHook";
import usePaymentHook from "@/shared/hooks/usePaymentHook";
import PaymentAndDelivery from "../PosMenu/PaymentAndDelivery";
import usePosPaymentHook from "@/shared/hooks/usePosPaymentHook";
import { FormProvider, useForm } from "react-hook-form";
import { useModalMeta } from "@/shared/atoms/useModal";
import usePaySessionHook from "@/shared/hooks/usePaySessionHook";
import usePosOrderUser from "@/shared/atoms/usePosOrderUser";
import { objectIsEmpty } from "@/shared/utils/helpers";
import useDidMountEffect from "@/shared/hooks/useDidMountEffect";
import useCashDroPaymentHook from "@/shared/hooks/useCashDroPaymentHook";
import usePosOrderTotalAmountHook from "@/shared/hooks/usePosOrderTotalAmountHook";
import useIsOrderEditingHook from "@/shared/hooks/useIsOrderEditingHook";
import {
  useSessionId,
  useSessionTotalAmount,
} from "@/shared/hooks/usePosOrderTableSessionHook";
import { PaymentMethodEnum } from "@/shared/utils/enum";
import { logCashDro } from "@/shared/utils/cashDro";
import usePosUserSettingsQuery from "@/shared/queries/pos/usePosUserSettingsQuery";
import useGetTerminals from "@/shared/queries/pos/useGetTerminals";
import useSavePosTerminal from "@/shared/mutations/pos/useSavePosTerminal";
import BackButton from "../Button/BackButton";
import Checkbox from "../Checkbox/Checkbox";

type PaymentStep = "methods" | "terminal";

const PosCartPaymentModal = ({ onExit }: ModalProps) => {
  const { t } = useTranslation();
  const [{ isSession, isChangePaymentMethod }] = useModalMeta();
  const methods = useForm();
  const { paymentList } = usePosPaymentHook();
  const [paymentId, setPaymentId] = usePosOrderPayment();
  const { handlePostOrder, isPending } = usePosOrderHook();
  const { isPaymentMethodUnavailable } = usePosPaymentHook();
  const paySession = usePaySessionHook();
  const [orderUser, setOrderUser] = usePosOrderUser();
  const { handleSubmit } = methods;
  const { isOnlinePayment } = usePaymentHook();
  const totalAmount = usePosOrderTotalAmountHook();
  const sessionTotalAmount = useSessionTotalAmount();
  const sessionId = useSessionId();
  const { isOrderEditing, editOrder } = useIsOrderEditingHook();
  const cashDro = useCashDroPaymentHook();
  const [isCashDroSelected, setIsCashDroSelected] = useState(false);
  const [step, setStep] = useState<PaymentStep>("methods");
  const [selectedTerminalId, setSelectedTerminalId] = useState<string | null>(
    null
  );
  const { data: posUserSettings, isSuccess: isPosUserSettingsLoaded } =
    usePosUserSettingsQuery();
  const { data: terminals = [] } = useGetTerminals();
  const { saveTerminal, isPending: isSavingTerminal } = useSavePosTerminal();
  const [saveTerminalAsDefault, setSaveTerminalAsDefault] = useState(true);

  // Only ask for a terminal once the settings are loaded and none is stored.
  const needsTerminalChoice =
    isPosUserSettingsLoaded && !posUserSettings?.default_pos_terminal_id;
  // With a single terminal there is nothing to choose: it is saved directly.
  const singleTerminal = terminals.length === 1 ? terminals[0] : undefined;
  const showTerminalChoice = needsTerminalChoice && terminals.length > 1;
  const initialPaymentId = useRef(paymentId);
  const isPaymentMethodSaved = useRef(false);

  useEffect(
    () => () => {
      if (isChangePaymentMethod && !isPaymentMethodSaved.current) {
        setPaymentId(initialPaymentId.current);
      }
    },
    [isChangePaymentMethod, setPaymentId]
  );

  const showCashDro = cashDro.isActive && !isChangePaymentMethod;
  const payWithCashDro = showCashDro && isCashDroSelected;

  const buttonCount = paymentList.length + (showCashDro ? 1 : 0);
  const gridCols =
    buttonCount === 2
      ? "grid grid-cols-2"
      : buttonCount % 2 === 1 || buttonCount === 6
        ? "grid sm:grid-cols-3 grid-cols-2"
        : "grid sm:grid-cols-4 grid-cols-2";

  useDidMountEffect(() => {
    finishPayment();
  }, [orderUser]);

  const onSubmit = (data) => {
    if (step === "terminal") {
      if (!selectedTerminalId) return;
      if (saveTerminalAsDefault) {
        saveTerminal(selectedTerminalId).then(finishPayment);
      } else {
        finishPayment();
      }
      return;
    }

    if (paymentId === PaymentMethodEnum.POS_TERMINAL && needsTerminalChoice) {
      if (singleTerminal) {
        saveTerminal(singleTerminal.pos_terminal_id).then(finishPayment);
        return;
      }
      if (showTerminalChoice) {
        setStep("terminal");
        return;
      }
    }

    if (objectIsEmpty(data)) {
      finishPayment();
    } else {
      setOrderUser((prev) => ({ ...prev, ...data }));
    }
  };

  const finishPayment = () => {
    if (isChangePaymentMethod) {
      isPaymentMethodSaved.current = true;
      onExit();
      return;
    }

    const pay = () => (isSession ? paySession() : handlePostOrder());

    if (paymentId === PaymentMethodEnum.CASH) {
      const orderId = editOrder?.order_information?.item_id;
      const alreadyPaid = isOrderEditing
        ? parseFloat(editOrder?.order_information?.total_paid || "0") || 0
        : 0;
      const amountDue = isSession
        ? sessionTotalAmount
        : totalAmount - alreadyPaid;

      const useCashDro = payWithCashDro && amountDue > 0;

      logCashDro("cash payment from the POS", {
        isCashDroActive: cashDro.isActive,
        payWithCashDro,
        isSession,
        sessionId,
        isOrderEditing,
        orderId,
        totalAmount,
        sessionTotalAmount,
        alreadyPaid,
        amountDue,
        decision: useCashDro
          ? "collect through CashDro"
          : payWithCashDro
            ? "nothing due, save without CashDro"
            : "CashDro not selected, normal cash flow",
      });

      if (useCashDro) {
        cashDro.collect({
          amount: amountDue,
          aliasId: isSession
            ? `S${sessionId}`
            : orderId
              ? `O${orderId}`
              : undefined,
          onPaid: pay,
        });
        return;
      }
    }

    pay();
  };

  return (
    <Modal onExit={onExit}>
      <ModalTitle>
        {isChangePaymentMethod ? t("changePaymentMethod") : t("payOrder")}
      </ModalTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {step === "terminal" && (
            <>
              <div className="mb-8 flex items-center justify-between gap-4">
                <BackButton onClick={() => setStep("methods")}>
                  {t("back")}
                </BackButton>
                <span className="text-text-tertiary">
                  {t("posOrders.selectPosTerminal")}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-4 sm:grid-cols-3 sm:gap-x-4 sm:gap-y-8">
                {terminals.map((terminal) => (
                  <PaymentAndDelivery
                    key={terminal.pos_terminal_id}
                    isSelect={terminal.pos_terminal_id === selectedTerminalId}
                    name={terminal.pos_terminal_name}
                    icon="startPin"
                    onClick={() =>
                      setSelectedTerminalId(terminal.pos_terminal_id)
                    }
                  />
                ))}
              </div>
              <div className="mt-8">
                <Checkbox
                  name="save_terminal_as_default"
                  checked={saveTerminalAsDefault}
                  active={saveTerminalAsDefault}
                  onChange={(e) => setSaveTerminalAsDefault(e.target.checked)}
                >
                  {t("posOrders.saveTerminalAsDefault")}
                </Checkbox>
              </div>
            </>
          )}
          <div
            className={cn(
              gridCols,
              "gap-x-2 gap-y-4 sm:gap-x-4 sm:gap-y-8",
              step === "terminal" && "hidden"
            )}
          >
            {showCashDro && (
              <PaymentAndDelivery
                isSelect={payWithCashDro}
                name={t("cashDro.title")}
                icon="cashDro"
                onClick={() => {
                  setPaymentId(PaymentMethodEnum.CASH);
                  setIsCashDroSelected(true);
                }}
              />
            )}
            {paymentList.map((p) => {
              return (
                <PaymentAndDelivery
                  key={p.value}
                  isSelect={p.value === paymentId && !payWithCashDro}
                  name={t(`posOrders.${p.translationKey}`)}
                  icon={p.icon as IconsVariant}
                  onClick={() => {
                    setPaymentId(p.value);
                    setIsCashDroSelected(false);
                    if (
                      p.value === PaymentMethodEnum.POS_TERMINAL &&
                      showTerminalChoice
                    ) {
                      setStep("terminal");
                    }
                  }}
                />
              );
            })}
          </div>
          <div
            className={cn(
              "flex justify-end transition-all duration-200 mt-12",
              isOnlinePayment(paymentId)
                ? "h-auto opacity-100"
                : "opacity-0 h-0"
            )}
          >
            <PosPaymentEmailOrSms isShown={isOnlinePayment(paymentId)} />
          </div>
          <PosCartOrderButton
            isDisabled={
              isPaymentMethodUnavailable ||
              (step === "terminal" && !selectedTerminalId)
            }
            isPending={isPending || isSavingTerminal}
            buttonType="submit"
          >
            {isChangePaymentMethod ? t("save") : t("payOrder")}
          </PosCartOrderButton>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default PosCartPaymentModal;
